import { useEffect, useState } from 'react'
import type { DataBundle, DraftRecord, SectionDef } from './types'
import { loadData } from './services/api'
import { Login } from './components/Login'
import { Dashboard } from './components/Dashboard'
import { GenericManager } from './components/GenericManager'
import { SessionManager } from './components/SessionManager'
import { ImageStudio } from './components/ImageStudio'
import { Academy } from './components/Academy'
import { Backups } from './components/Backups'
import { sectionById } from './data/schemas'

const emptyData: DataBundle = { students: [], sessions: [], files: {}, history: [], images: [], metadata: [] }
type View = 'home' | 'images' | 'academy' | 'backups'

export default function App() {
  const [token, setToken] = useState(() => sessionStorage.getItem('gestor-v2-token') || '')
  const [data, setData] = useState<DataBundle>(emptyData)
  const [view, setView] = useState<View>('home')
  const [section, setSection] = useState<SectionDef | null>(null)
  const [academyModule, setAcademyModule] = useState<string | undefined>()
  const [draftToOpen, setDraftToOpen] = useState<DraftRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  async function refresh(): Promise<void> {
    if (!token) return
    setLoading(true)
    setMessage('')
    try { setData(await loadData(token)) }
    catch (error) {
      const text = error instanceof Error ? error.message : 'No fue posible cargar Google Sheets.'
      setMessage(text)
      if (/venció|válida/i.test(text)) {
        sessionStorage.removeItem('gestor-v2-token')
        setToken('')
      }
    } finally { setLoading(false) }
  }

  useEffect(() => { refresh() }, [token])
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (document.querySelector('.form-card')) { event.preventDefault(); event.returnValue = '' }
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [])

  if (!token) return <Login onSuccess={(value) => { sessionStorage.setItem('gestor-v2-token', value); setToken(value) }}/>

  function openSection(next: SectionDef): void { setDraftToOpen(null); setSection(next); setView('home') }
  function openDraft(draft: DraftRecord): void {
    const next = sectionById(draft.sectionId)
    if (!next) { setMessage('El borrador corresponde a una sección que ya no existe.'); return }
    setDraftToOpen(draft)
    setSection(next)
    setView('home')
  }
  function openView(next: View): void { setDraftToOpen(null); setSection(null); setView(next) }
  function openAcademy(module?: string): void { setAcademyModule(module); openView('academy') }
  function logout(): void { sessionStorage.removeItem('gestor-v2-token'); setToken('') }

  return <div className="app-shell">
    <aside className="sidebar"><div><button className="brand" onClick={() => openView('home')}><span>GH</span><strong>Gestor<br/>Pedagógico</strong></button><nav><button className={!section && view === 'home' ? 'active' : ''} onClick={() => openView('home')}>Inicio</button><button className={!section && view === 'images' ? 'active' : ''} onClick={() => openView('images')}>Imágenes WebP</button><button className={!section && view === 'academy' ? 'active' : ''} onClick={() => openAcademy()}>Academia</button><button className={!section && view === 'backups' ? 'active' : ''} onClick={() => openView('backups')}>Respaldos</button></nav></div><div className="sidebar-bottom"><button className="secondary full" onClick={refresh}>Actualizar desde Sheets</button><button className="logout" onClick={logout}>Cerrar sesión</button></div></aside>
    <main className="main-content">
      {loading && <div className="loading">Cargando información desde Google Sheets…</div>}
      {message && <div className="alert error">{message}<button onClick={() => setMessage('')}>×</button></div>}
      {section ? (
        section.id === 'sessions'
          ? <SessionManager key={`${section.id}:${draftToOpen?.id || 'new'}`} section={section} data={data} token={token} initialDraft={draftToOpen} onData={setData} onBack={() => { setDraftToOpen(null); setSection(null) }} onOpenAcademy={() => openAcademy('sesiones')}/>
          : <GenericManager key={`${section.id}:${draftToOpen?.id || 'new'}`} section={section} data={data} token={token} initialDraft={draftToOpen} onData={setData} onBack={() => { setDraftToOpen(null); setSection(null) }} onOpenAcademy={openAcademy}/>
      ) : view === 'home' ? <Dashboard data={data} onOpen={openSection} onOpenDraft={openDraft} onImages={() => openView('images')} onAcademy={() => openAcademy()}/>
        : view === 'images' ? <ImageStudio data={data} token={token} onData={setData} onOpenAcademy={() => openAcademy('imagenes')}/>
          : view === 'academy' ? <Academy initialModule={academyModule}/>
            : <Backups data={data}/>}
    </main>
  </div>
}
